import { useOrderingMessage } from "../useOrderingMessage";
import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  MinusCircleOutlined,
  ScissorOutlined,
  TeamOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  PercentageOutlined,
  ToolOutlined,
  UndoOutlined,
  EditOutlined,
  UserSwitchOutlined,
  TagOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { CloseTableModal } from "./payment/CloseTableModal";
import { useClosingBills } from "./payment/closingBills";
import { closeModeOf, useCloseBillTarget } from "./payment/useCloseBillTarget";
import { isClosedEmptyBill } from "./schema/orderingFloorData";
import { RenameTableModal } from "./manage/RenameTableModal";
import {
  CancelItemsModal,
  type CancelItemsOutcome,
  type CancelItemsSelection,
} from "./manage/CancelItemsModal";
import { SplitBillModal } from "./split/SplitBillModal";
import { TransferBillFlow } from "./manage/TransferBillFlow";
import type { SecondaryAction } from "./OptionsPopover";
import { TableFooterBar } from "./footer/TableFooterBar";
import { mainFooterButtons } from "./footer/mainFooterButtons";
import { useTableFooterShortcuts } from "./useTableFooterShortcuts";
import { ClientsModal } from "./clients/ClientsModal";
import { ItemDiscountsModal } from "./discounts/ItemDiscountsModal";
import { requestedBillDiscount } from "./discounts/billAmountDiscount";
import { CustomPriceModal } from "./pricing/CustomPriceModal";
import { ReportTypeSelectionModal } from "./reports/ReportTypeSelectionModal";
import { GeneratePersonalReportModal } from "./reports/GeneratePersonalReportModal";
import { GenerateEstablishmentReportModal } from "./reports/GenerateEstablishmentReportModal";
import { OperationsModal } from "./operations/OperationsModal";
import { useInvoicingFlow } from "./invoicing/useInvoicingFlow";
import { FREE_INVOICE_PERMISSION } from "../../../../api/invoices";
import { useFiscalInvoiceClose } from "./invoicing/useFiscalInvoiceClose";
import { printPreliminaryBill } from "../../../../api/bills";
import { useTableGuests } from "./guests/useTableGuests";
import { getApiErrorMessage } from "../../../../utils/translations";
import { RestoreBillModal } from "./manage/RestoreBillModal";
import { useSeatReservationAction } from "./seat/useSeatReservationAction";
import { useInterimReportAction } from "./reports/useInterimReportAction";
import { useDepositAction } from "./deposits/useDepositAction";
import { useHeldOrdersConfirm } from "./kitchenHold/useHeldOrdersConfirm";
import { heldElementsOf } from "../../../../lib/kitchenHold";
import { ForkKnifeIcon } from "../../lunch/ForkKnifeIcon";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { setSelectedBillId } from "../../../../store";
import {
  useBills,
  useCancelBillItems,
} from "../../../../hooks/useBills";
import { usePrinterNotifications } from "../../../../hooks/usePrinterNotifications";
import { useConfiguration } from "../../../../hooks/useConfiguration";
import { useBillDiscountLocked, useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import type { RecordedBillTip } from "./payment/useBillCloseTip";
import type { RecordedBillDeposit } from "./payment/useCloseDeposit";

interface TableFooterProps {
  onNewTable?: () => void;
  onAddToTable?: () => void;
  /** Режим „Схема": „Нова маса" е активна само за избрана маса от схемата без сметка, която
   *  приема поръчки (свободна или сметката ѝ се закрива); иначе е заключена (и F5 мълчи). */
  newTableDisabled?: boolean;
  /** Вляво в лентата — часовникът в режим „Схема" (там няма релса с маси). */
  leading?: ReactNode;
  selectedTableId: number | null;
  selectedTableName?: string | null;
  onClearSelection?: () => void;
  onRenameModalOpenChange?: (open: boolean) => void;
  onAnyModalOpenChange?: (open: boolean) => void;
  /** Бързо закриване: id на сметката, за която да се отвори модалът „Закрий"
   *  веднага щом се появи в списъка (след „Поръчай"). Родителят го чисти. */
  autoCloseBillId?: number | null;
  onAutoCloseHandled?: () => void;
  /** „Настани" (резервация) е отворил/закачил сметката — родителят я избира
   *  и влиза в менюто за маркиране. */
  onReservationSeated?: (billId: number) => void;
}

export const TableFooter = ({
  onNewTable,
  onAddToTable,
  newTableDisabled = false,
  leading,
  selectedTableId,
  selectedTableName,
  onClearSelection,
  onRenameModalOpenChange,
  onAnyModalOpenChange,
  autoCloseBillId,
  onAutoCloseHandled,
  onReservationSeated,
}: TableFooterProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { notifyPrinterError } = usePrinterNotifications();
  const dispatch = useAppDispatch();
  const permissions = useAppSelector((state) => state.auth.permissions);
  // „Закриване веднага след поръчка": при нея модалът „Закрий" изскача преди
  // сервитьорът да е имал шанс да мине през „Опции → Клиенти" — затова там
  // получава собствен бутон „Клиенти" (само с правото select_customer).
  const { configuration } = useConfiguration();
  const quickCloseEnabled = configuration.orderingQuickClose;
  // Локален режим (docs/edge.md): действията извън обхвата на касата
  // („Раздели", „Отчет", „Фактуриране", „Възстанови") остават видими, но
  // disabled с подсказка; същото важи за клавишите им. Клиенти, отстъпки,
  // промяна на цена, прехвърляне, операции и „Закрий" на закрита без бон
  // сметка (/close/retry) касата сервира — те са свободни.
  const edgeLocal = useIsEdgeLocal();
  const offlineLock = edgeLocal ? t("edge.unavailableOffline") : undefined;

  const canOpenBill = permissions.some((p) => p.name === "open_bill");
  const canVoidItem = permissions.some((p) => p.name === "void_item");
  const canCloseBill = permissions.some((p) => p.name === "close_bill");
  const canSplitBill = permissions.some((p) => p.name === "split_bill");
  const canPaymentCash = permissions.some((p) => p.name === "payment_cash");
  const canPaymentCard = permissions.some((p) => p.name === "payment_card");
  const canCloseBill1 = permissions.some((p) => p.name === "close_bill_1");
  const canSelectCustomer = permissions.some(
    (p) => p.name === "select_customer",
  );
  const canItemDiscount = permissions.some(
    (p) => p.name === "set_discount_percent",
  );
  const canSetCustomPrice = permissions.some(
    (p) => p.name === "set_custom_price",
  );
  const canPreliminaryBill = permissions.some(
    (p) => p.name === "preliminary_bill",
  );
  const canFiscalOperations = permissions.some(
    (p) => p.name === "fiscal_operations",
  );
  const canRestoreLastBill = permissions.some(
    (p) => p.name === "restore_last_bill",
  );
  const canChangePaymentMethod = permissions.some(
    (p) => p.name === "change_payment_method",
  );
  const canChangeTable = permissions.some((p) => p.name === "change_table");
  const canTransferBill = permissions.some((p) => p.name === "transfer_bill");
  const canPersonalReport = permissions.some(
    (p) => p.name === "personal_report",
  );
  const canInvoicing = permissions.some((p) => p.name === "invoicing");
  const canFreeInvoice = permissions.some((p) => p.name === FREE_INVOICE_PERMISSION);
  const canEstablishmentReport = permissions.some(
    (p) => p.name === "establishment_report",
  );
  const canAnyReport = canPersonalReport || canEstablishmentReport;
  const canManageLunchMenu = permissions.some((p) => p.name === "manage_lunch_menu");
  const navigate = useNavigate();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [isClientsModalOpen, setIsClientsModalOpen] = useState(false);
  const [isItemDiscountModalOpen, setIsItemDiscountModalOpen] = useState(false);
  const [isCustomPriceModalOpen, setIsCustomPriceModalOpen] = useState(false);
  const [isReportTypeModalOpen, setIsReportTypeModalOpen] = useState(false);
  const [isPersonalReportModalOpen, setIsPersonalReportModalOpen] =
    useState(false);
  const [isEstablishmentReportModalOpen, setIsEstablishmentReportModalOpen] =
    useState(false);
  const [isOperationsModalOpen, setIsOperationsModalOpen] = useState(false);
  // Печатът на предварителна сметка минава през брокера и чака принтера —
  // на бавна серийна връзка това са секунди, през които бутонът трябва да
  // е зает, иначе нетърпеливо второ натискане пуска втори лист.
  const [isPrintingPreliminaryBill, setIsPrintingPreliminaryBill] =
    useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  // Сметка от „Възстановяване", маркирана за фискализиране: отваря „Закрий"
  // (mode="retry") за нея — операторът пак избира метод на плащане. Данните
  // идват от историята, защото закрита нефискализирана сметка може изобщо да
  // не е в списъка с маси (non_fiscal_display="never").
  const [fiscalizeBill, setFiscalizeBill] = useState<{
    id: number;
    name: string;
    totalAmount: string;
    tip: RecordedBillTip;
    deposit: RecordedBillDeposit | null;
  } | null>(null);
  // Новата (възстановена) сметка, за която чакаме да отворим модала „Закрий"
  // след „Смяна на начин на плащане" в модала за възстановяване.
  const [changePaymentBillId, setChangePaymentBillId] = useState<number | null>(
    null,
  );
  const [isTransferWaiterModalOpen, setIsTransferWaiterModalOpen] =
    useState(false);
  const [isRenameModalOpen, setIsRenameModalOpenState] = useState(false);
  const setIsRenameModalOpen = useCallback(
    (next: boolean) => {
      setIsRenameModalOpenState(next);
      onRenameModalOpenChange?.(next);
    },
    [onRenameModalOpenChange],
  );

  // „Настани" — модалът живее в seat/ (docs/ordering.md).
  const seatReservation = useSeatReservationAction({
    onSeated: (billId) => onReservationSeated?.(billId),
  });
  // „Междинен отчет" — кодът и прегледът живеят в reports/.
  const interimReport = useInterimReportAction();

  const names = useAppSelector((state) => state.auth.names);
  const { data: bills = [] } = useBills({ fromCacheOnly: true });
  const cancelBillItemsMutation = useCancelBillItems();

  const selectedBill = bills.find((bill) => bill.id === selectedTableId);
  const hasSelectedBill = !!selectedBill;
  // Закрита сметка без касова бележка. Бутонът „Закрий" отваря СЪЩИЯ модал —
  // със същите методи и същия избор — само че заявката отива към
  // `/close/retry`, защото сметката вече е закрита.
  const isClosedEmpty = !!selectedBill && isClosedEmptyBill(selectedBill);
  // Закриването на избраната маса още чака принтера (кутийката ѝ се върти):
  // нито поръчка, нито второ закриване, нито друго действие по нея — като
  // докато модалът стоеше отворен със спинера (payment/backgroundBillClose.ts).
  const { closingBillIds } = useClosingBills();
  const isSelectedBillClosing =
    selectedTableId != null && closingBillIds.has(selectedTableId);
  const isActionDisabled = isClosedEmpty || isSelectedBillClosing;
  const operatorName = names || "N/A";
  const totalAmount = selectedBill?.total_amount || "0.00";

  // „Брой гости" (Конфигурация → Маркиране): питането преди „Закрий" и
  // бутонът в „Опции" — guests/useTableGuests.
  const { askBeforeClose, guestsAction, guestCountModal, guestCountModalOpen } =
    useTableGuests({
      selectedBill,
      canOpenBill,
      canCloseBill,
      isActionDisabled,
      offlineLock,
    });

  // Задържани поръчки по сметката: закриването ги пуска в кухнята —
  // питането е преди „Брой гости" и преди модала (kitchenHold/).
  const { confirmHeldOrders, heldOrdersModal, heldOrdersConfirmOpen } =
    useHeldOrdersConfirm();

  // „Закрий" е за сметката от момента на натискането, не за избраната след
  // въпроса „Брой гости" (payment/useCloseBillTarget).
  const {
    closeTarget,
    closeTargetBill,
    closeModalOpen,
    requestClose,
    requestFiscalInvoiceClose,
    dismissClose,
  } = useCloseBillTarget({
    selectedBillId: selectedTableId,
    bills,
    // Без задържани — синхронно, точно както преди (тестовете на целта го пазят).
    askBeforeClose: (bill) => {
      const heldCount = heldElementsOf(bill).length;
      if (heldCount === 0) return askBeforeClose(bill);
      return confirmHeldOrders(heldCount).then(
        (proceed) => proceed && askBeforeClose(bill),
      );
    },
    onTargetGone: (bill) =>
      message.warning(t("closeTable.billNoLongerOpen", { table: bill.name })),
  });
  // Отстъпка в € на цялата сметка в локален режим с каса, която НЕ я умее
  // (`capabilities.bill_discount`): тя би издала бон без нея — „Закрий" чака
  // облака (бутонът — с подсказка, F7 и авто-закриването — с предупреждение).
  const billDiscountLocked = useBillDiscountLocked();
  const closeLockedOffline =
    billDiscountLocked && requestedBillDiscount(selectedBill) > 0;
  const handleCloseClick = useCallback(async () => {
    if (closeLockedOffline) {
      message.warning(t("itemDiscounts.billCloseOffline"));
      return;
    }
    await requestClose();
  }, [closeLockedOffline, requestClose, message, t]);

  // „Фактуриране" → „Текуща сметка" (избраната маса) → купувач → този „Закрий"
  // с фискална фактура (invoicing/useFiscalInvoiceClose) — само с правото да закрива.
  const requestFiscalInvoice = useFiscalInvoiceClose({
    selectedBillId: selectedTableId,
    hasSelectedBill,
    requestFiscalInvoiceClose,
  });
  // „Фактуриране" — изборът, формата, ПОС печатът и прегледът (invoicing/).
  const invoicing = useInvoicingFlow({
    onFiscalInvoiceBuyer: canCloseBill ? requestFiscalInvoice : undefined,
    selectedBill,
    isSelectedBillClosing,
    canFreeInvoice,
  });

  // „Депозит" — „Нов депозит" / „Използвай депозит" (deposits/); с
  // „Фактуриране" новият депозит с готов бон отваря фактурата за него.
  const depositAction = useDepositAction({
    selectedBill,
    isSelectedBillClosing,
    onInvoiceDeposit: invoicing.openDepositInvoice,
  });
  // Затворен модалът стои монтиран (методите на плащане се зареждат отрано):
  // до първото „Закрий" на избраната, после на последната си цел.
  const closeModalBill = closeTargetBill ?? selectedBill ?? null;

  // Агрегиран сигнал нагоре — дали който и да е от модалите на footer-а е отворен.
  // Родителят го подава на TableList, за да изключи глобалната клавиатурна търсачка.
  const anyFooterModalOpen =
    closeModalOpen ||
    isCancelModalOpen ||
    isSplitModalOpen ||
    isClientsModalOpen ||
    isItemDiscountModalOpen ||
    isCustomPriceModalOpen ||
    isReportTypeModalOpen ||
    isPersonalReportModalOpen ||
    isEstablishmentReportModalOpen ||
    isOperationsModalOpen ||
    invoicing.isOpen ||
    isRestoreModalOpen ||
    fiscalizeBill !== null ||
    isTransferWaiterModalOpen ||
    isRenameModalOpen ||
    guestCountModalOpen ||
    heldOrdersConfirmOpen ||
    seatReservation.open ||
    interimReport.open ||
    depositAction.open;
  useEffect(() => {
    onAnyModalOpenChange?.(anyFooterModalOpen);
  }, [anyFooterModalOpen, onAnyModalOpenChange]);


  const handleReportClick = useCallback(() => {
    if (canPersonalReport && !canEstablishmentReport) {
      setIsPersonalReportModalOpen(true);
    } else if (canEstablishmentReport && !canPersonalReport) {
      setIsEstablishmentReportModalOpen(true);
    } else {
      setIsReportTypeModalOpen(true);
    }
  }, [canPersonalReport, canEstablishmentReport]);

  // Бързо закриване: щом поръчаната сметка се появи в списъка (и е избраната),
  // отваряме модала „Закрий" веднъж и сигнализираме на родителя да изчисти флага.
  useEffect(() => {
    if (autoCloseBillId == null) return;
    if (!canCloseBill) {
      onAutoCloseHandled?.();
      return;
    }
    // изчакай bills да се презареди и сметката да стане избраната
    if (selectedTableId !== autoCloseBillId || !hasSelectedBill) return;
    void handleCloseClick();
    onAutoCloseHandled?.();
  }, [
    autoCloseBillId,
    selectedTableId,
    hasSelectedBill,
    canCloseBill,
    handleCloseClick,
    onAutoCloseHandled,
  ]);

  // „Смяна на начин на плащане": restore-ът вече е минал (със сторно бон, ако
  // сметката е била фискализирана) и е създал НОВА отворена сметка. Изчакваме
  // тя да се появи в списъка и да стане избраната, и отваряме „Закрий" за нея,
  // за да се избере новият начин на плащане без ръчни стъпки.
  useEffect(() => {
    if (changePaymentBillId == null) return;
    // Операторът е избрал друга маса, докато списъкът се презарежда — отказваме
    // се, вместо да отворим „Закрий" изневиделица по-късно.
    if (selectedTableId != null && selectedTableId !== changePaymentBillId) {
      setChangePaymentBillId(null);
      return;
    }
    if (selectedTableId !== changePaymentBillId || !hasSelectedBill) return;
    void handleCloseClick();
    setChangePaymentBillId(null);
  }, [
    changePaymentBillId,
    selectedTableId,
    hasSelectedBill,
    handleCloseClick,
  ]);

  const openSplitModal = useCallback(() => setIsSplitModalOpen(true), []);

  // F5 Нова · F6 Добави · F7 Закрий · F2 Отчет · F10 Раздели
  useTableFooterShortcuts({
    canOpenBill,
    canCloseBill,
    canSplitBill,
    canAnyReport,
    hasSelectedBill,
    isActionDisabled,
    isClosing: isSelectedBillClosing,
    edgeLocal,
    newTableDisabled,
    onNewTable,
    onAddToTable,
    onClose: handleCloseClick,
    onReport: handleReportClick,
    onSplit: openSplitModal,
  });

  // Сторно на един или повече редове — ЕДНА заявка за всички избрани
  // (`/cancel-items`): сървърът ги сторнира заедно (всичко или нищо) и печата
  // ЕДИН кухненски билет на станция. Стар сървър без адреса → ред по ред
  // (`api/billsCancel.ts`), както преди. Пълно и частично количество.
  const handleCancelItems = async (
    data: CancelItemsSelection,
  ): Promise<CancelItemsOutcome | void> => {
    if (!selectedTableId) return;

    try {
      await cancelBillItemsMutation.mutateAsync({
        billId: selectedTableId,
        data: {
          items: data.elements.map((element) => ({
            billElementId: element.billElementId,
            billElementItemId: element.billElementItemId,
            quantity: element.quantity,
          })),
          reason: data.reason,
          isDefective: data.isDefective,
        },
      });

      if (onClearSelection) {
        onClearSelection();
      }
    } catch (err: unknown) {
      const text = getApiErrorMessage(err, t("common.error"));
      message.error(text);
      // ВРЪЩАМЕ отказа, а не само го показваме: прозорецът затваря само при
      // успех (`CancelItemsOutcome`). Без този `return` пропадналото сторно
      // изглеждаше точно като минало — прозорецът се затваряше, селекцията
      // се губеше и оставаше само едно съобщение, което си отива само.
      return { ok: false, error: text };
    }
  };


  // Предварителна сметка — печат на POS принтера на оператора. Не издава
  // и не фискализира нищо, затова е свободно повторим: при грешка се
  // натиска пак. Без диалог за потвърждение — за разлика от фактурата и
  // отчетите, тук няма какво да се обърка, а сервитьорът иска листа
  // веднага, докато е на масата.
  const handlePreliminaryBill = useCallback(async () => {
    if (!selectedTableId || isPrintingPreliminaryBill) return;
    setIsPrintingPreliminaryBill(true);
    // Popover-ът се затваря при клика, тоест бутонът вече го няма и spinner
    // върху него не би се видял. Затова прогресът е toast с фиксиран key,
    // който после се ПОДМЕНЯ от изхода — на бавна серийна връзка печатът е
    // секунди и мълчанието изглежда като „бутонът не работи".
    const key = "preliminary-bill";
    message.open({
      key,
      type: "loading",
      content: t("ordering.preliminaryBillPrinting"),
      duration: 0,
    });
    try {
      await printPreliminaryBill(selectedTableId);
      message.open({
        key,
        type: "success",
        content: t("ordering.preliminaryBillSuccess"),
      });
    } catch (error: unknown) {
      // Loading toast-ът се маха ръчно — изходът вече не е message със същия
      // key, а постоянна принтерна нотификация с ръчно затваряне.
      message.destroy(key);
      notifyPrinterError(
        getApiErrorMessage(error, t("ordering.preliminaryBillError")),
      );
    } finally {
      setIsPrintingPreliminaryBill(false);
    }
  }, [
    selectedTableId,
    isPrintingPreliminaryBill,
    message,
    notifyPrinterError,
    t,
  ]);

  // Вторичните действия събираме в списък — стоят в „Опции“, а довлечените от
  // потребителя и в лентата (footer/TableFooterBar); `key` е записаният ключ в
  // личната настройка footerActions — не го преименувай, иначе бутонът се връща в „Опции“.
  const secondaryActions = [
    canVoidItem && {
      key: "cancel",
      label: t("ordering.cancelItem"),
      icon: <MinusCircleOutlined />,
      // Сторното вече не изисква избран продукт — модалът показва всички
      // поръчани редове и в него се избира кои/колко да се сторнират.
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsCancelModalOpen(true),
    },
    canSplitBill && {
      key: "split",
      lockedTooltip: offlineLock,
      label: t("ordering.split"),
      icon: <ScissorOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsSplitModalOpen(true),
      shortcut: "F10",
    },
    canChangeTable && {
      key: "rename",
      label: t("ordering.renameTable"),
      icon: <EditOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsRenameModalOpen(true),
    },
    guestsAction,
    canTransferBill && {
      key: "transferWaiter",
      label: t("ordering.transferBill"),
      icon: <UserSwitchOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsTransferWaiterModalOpen(true),
    },
    canSelectCustomer && {
      key: "clients",
      label: t("ordering.clients"),
      icon: <TeamOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      // Заключен ред НЕ блокира избора на клиент — отстъпката е на ниво
      // сметка и BE така или иначе прескача редовете със заключена цена.
      onClick: () => setIsClientsModalOpen(true),
    },
    canItemDiscount && {
      key: "discounts",
      label: t("ordering.discounts"),
      icon: <PercentageOutlined />,
      // Не иска избран продукт — модалът показва всички редове на сметката
      // с общ процент и избор на бройки по ред.
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsItemDiscountModalOpen(true),
    },
    canSetCustomPrice && {
      key: "customPrice",
      // Не иска избран продукт — модалът показва всички редове на сметката
      // (продукти и добавки), всеки със собствен молив за нова цена.
      label: t("ordering.customPrice"),
      icon: <TagOutlined />,
      disabled: !hasSelectedBill || isActionDisabled,
      onClick: () => setIsCustomPriceModalOpen(true),
    },
    canPreliminaryBill && {
      key: "preliminaryBill",
      label: t("ordering.preliminaryBill"),
      icon: <PrinterOutlined />,
      // Затворена сметка вече има фискален бон — BE-то връща 409, но по-добре
      // бутонът да е сив, отколкото натискането да върне грешка.
      disabled:
        !hasSelectedBill ||
        isActionDisabled ||
        selectedBill?.status !== "open" ||
        isPrintingPreliminaryBill,
      onClick: handlePreliminaryBill,
    },
    canAnyReport && {
      key: "report",
      lockedTooltip: offlineLock,
      label: t("ordering.report"),
      icon: <FileTextOutlined />,
      onClick: handleReportClick,
      shortcut: "F2",
    },
    interimReport.action,
    depositAction.action,
    canFiscalOperations && {
      key: "operations",
      label: t("ordering.operations"),
      icon: <ToolOutlined />,
      onClick: () => setIsOperationsModalOpen(true),
    },
    canInvoicing && {
      key: "invoicing",
      lockedTooltip: offlineLock,
      label: t("ordering.invoicing"),
      icon: <FileDoneOutlined />,
      // първо текущата фирма — `invoicing/useOpenInvoicing.ts`
      onClick: invoicing.openInvoicing,
    },
    seatReservation.action,
    // „Обедно меню" — отделният екран /ordering/lunch-menu; извън обхвата на
    // касата → заключен в локален режим.
    canManageLunchMenu && {
      key: "lunchMenu",
      lockedTooltip: offlineLock,
      label: t("lunchMenu.title"),
      icon: <ForkKnifeIcon />,
      onClick: () => navigate("/ordering/lunch-menu"),
    },
    canRestoreLastBill && {
      key: "restore",
      lockedTooltip: offlineLock,
      label: t("ordering.restore"),
      icon: <UndoOutlined />,
      onClick: () => setIsRestoreModalOpen(true),
    },
  ].filter(Boolean) as SecondaryAction[];

  const mainButtons = mainFooterButtons({
    t,
    canOpenBill,
    canCloseBill,
    hasSelectedBill,
    isActionDisabled,
    isSelectedBillClosing,
    newTableDisabled,
    onNewTable,
    onAddToTable,
    onClose: handleCloseClick,
    closeLockedTooltip: closeLockedOffline
      ? t("itemDiscounts.billCloseOffline")
      : undefined,
  });

  return (
    <>
      <ReportTypeSelectionModal
        open={isReportTypeModalOpen}
        onClose={() => setIsReportTypeModalOpen(false)}
        onSelectPersonal={() => {
          setIsReportTypeModalOpen(false);
          setIsPersonalReportModalOpen(true);
        }}
        onSelectEstablishment={() => {
          setIsReportTypeModalOpen(false);
          setIsEstablishmentReportModalOpen(true);
        }}
      />

      <GeneratePersonalReportModal
        open={isPersonalReportModalOpen}
        onClose={() => setIsPersonalReportModalOpen(false)}
      />

      <GenerateEstablishmentReportModal
        open={isEstablishmentReportModalOpen}
        onClose={() => setIsEstablishmentReportModalOpen(false)}
      />

      <OperationsModal
        open={isOperationsModalOpen}
        onClose={() => setIsOperationsModalOpen(false)}
      />

      {invoicing.modals}

      <RestoreBillModal
        open={isRestoreModalOpen}
        onClose={() => setIsRestoreModalOpen(false)}
        canChangePaymentMethod={canChangePaymentMethod}
        onRestoredForPaymentChange={(billId) => {
          dispatch(setSelectedBillId(billId));
          setChangePaymentBillId(billId);
        }}
        onFiscalize={canCloseBill ? setFiscalizeBill : undefined}
      />

      {/* „Фискализирай" от „Възстановяване": същият модал „Закрий" като при
          closed-empty маса, само че за сметка от историята. */}
      {fiscalizeBill && (
        <CloseTableModal
          open
          onClose={() => setFiscalizeBill(null)}
          billId={fiscalizeBill.id}
          tableName={fiscalizeBill.name}
          totalAmount={fiscalizeBill.totalAmount}
          operatorName={operatorName}
          canPaymentCash={canPaymentCash}
          canPaymentCard={canPaymentCard}
          canCloseBill1={canCloseBill1}
          mode="retry"
          recordedTip={fiscalizeBill.tip}
          recordedDeposit={fiscalizeBill.deposit}
          // F7 отваря и „Закрий" за избраната маса ВЪРХУ този модал (F-клави-
          // шите не гледат отворени модали) — двата слушат Enter на window,
          // затова при двойно отваряне клавиатурата се изключва и в двата.
          keyboardDisabled={closeModalOpen}
        />
      )}

      {closeModalBill && (
        <CloseTableModal
          open={closeModalOpen}
          onClose={dismissClose}
          billId={closeModalBill.id}
          tableName={closeModalBill.name || "N/A"}
          totalAmount={closeModalBill.total_amount || "0.00"}
          operatorName={operatorName}
          canPaymentCash={canPaymentCash}
          canPaymentCard={canPaymentCard}
          canCloseBill1={canCloseBill1}
          customerId={closeModalBill.customer_id ?? null}
          bill={closeModalBill}
          mode={closeTarget?.mode ?? closeModeOf(closeModalBill)}
          fiscalInvoiceBuyer={closeTarget?.fiscalInvoiceBuyer ?? null}
          onOpenClients={
            quickCloseEnabled && canSelectCustomer
              ? () => setIsClientsModalOpen(true)
              : undefined
          }
          keyboardDisabled={isClientsModalOpen || fiscalizeBill !== null}
        />
      )}

      {selectedTableId && (
        <>
          <CancelItemsModal
            open={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            onConfirm={handleCancelItems}
            bill={selectedBill}
          />
          <SplitBillModal
            open={isSplitModalOpen}
            onClose={() => setIsSplitModalOpen(false)}
            billId={selectedTableId}
            bill={selectedBill}
          />
          <RenameTableModal
            open={isRenameModalOpen}
            billId={selectedTableId}
            currentName={selectedTableName || ""}
            onClose={() => setIsRenameModalOpen(false)}
          />
          <TransferBillFlow
            open={isTransferWaiterModalOpen}
            onClose={() => setIsTransferWaiterModalOpen(false)}
            billId={selectedTableId}
            tableName={selectedTableName || ""}
            currentWaiterId={selectedBill?.waiter_id ?? null}
            totalAmount={totalAmount}
          />
        </>
      )}

      {seatReservation.modal}

      {interimReport.modal}

      {depositAction.modal}

      {/* „Задържани поръчки" и „Брой гости" преди „Закрий" */}
      {heldOrdersModal}
      {guestCountModal}

      {/* Отворен от „Закрий" (бързо закриване) — за сметката на модала. */}
      <ClientsModal
        open={isClientsModalOpen}
        onClose={() => setIsClientsModalOpen(false)}
        billId={closeModalOpen && closeTarget ? closeTarget.billId : selectedTableId}
      />

      <ItemDiscountsModal
        open={isItemDiscountModalOpen}
        onClose={() => setIsItemDiscountModalOpen(false)}
        billId={selectedTableId}
        bill={selectedBill}
      />

      <CustomPriceModal
        open={isCustomPriceModalOpen}
        onClose={() => setIsCustomPriceModalOpen(false)}
        billId={selectedTableId}
        bill={selectedBill}
      />

      {/* Лентата (Нова · Добави · Закрий · изнесените от „Опции" · Опции) и
          влаченето между „Опции" и лентата — footer/TableFooterBar */}
      <TableFooterBar
        mainButtons={mainButtons}
        secondaryActions={secondaryActions}
        leading={leading}
      />
    </>
  );
};
